import axios from 'axios';

import Decimal from 'app/src/data/base/Decimal';
import { getCurrency } from 'helpers/locale';
import { AggregateSaleData } from 'root/apps/QuoteMachineIntegration/state/AggregateSaleData';
import {
  Customer as PaymentRequestCustomer,
  Discount as PaymentRequestDiscount,
  DiscountType,
  Fee,
  LineItem,
  Location,
  PricingTable,
  PricingTableType,
  PricingTableTypes,
  Tax as PaymentRequestTax,
  Totals,
  WorkOrder,
} from 'root/apps/QuoteMachineIntegration/state/PaymentRequest';
import { datadogRum } from 'root/datadog/datadogRum';

export async function buildPricingTable(
  type: PricingTableType,
  sale: AggregateSaleData,
  accountID: string
): Promise<PricingTable> {
  const currency = getCurrency()?.currencyCode;
  const saleLines = getSaleLinesPerPricingTableType(type, sale);
  const lineItems = buildLineItems(saleLines);
  const workOrders = buildWorkOrders(saleLines);
  const tableTaxes = await buildAllTaxes(type, sale, accountID);
  const tableTotals = buildTotals(type, sale);
  const tableFees = getAllFeesForTable(type, sale);
  const tableDiscounts = getAllDiscountsForTable(type, sale);

  return {
    currency,
    title: type,
    lineItems,
    workOrders,
    taxes: tableTaxes,
    discounts: tableDiscounts,
    fees: tableFees,
    ...tableTotals,
  };
}

export function buildTotals(pricingTableType: PricingTableType, sale: AggregateSaleData): Totals {
  switch (pricingTableType) {
    case PricingTableTypes.LAYAWAY:
      const layawayLines = getSaleLinesPerPricingTableType(pricingTableType, sale);
      const layawayFeeTotal = calculateFeeTotal(layawayLines);
      return {
        subtotal: toCents(sale?.Sale?.Customer?.m?.layawaysSubtotal)
          ? toCents(sale?.Sale?.Customer?.m?.layawaysSubtotal) - layawayFeeTotal
          : 0,
        taxTotal: toCents(sale?.Sale?.Customer?.m?.layawaysTaxTotal) || 0,
        discountTotal: toCents(sale?.Sale?.Customer?.m?.layawaysAllDiscounts) || 0,
        feeTotal: layawayFeeTotal,
        totalAmount: toCents(sale?.Sale?.Customer?.m?.layawaysTotal) || 0,
      };
    case PricingTableTypes.SPECIAL_ORDER:
      const specialOrderLines = getSaleLinesPerPricingTableType(pricingTableType, sale);
      const specialOrderFeeTotal = calculateFeeTotal(specialOrderLines);
      return {
        subtotal: toCents(sale?.Sale?.Customer?.m?.specialOrdersSubtotal)
          ? toCents(sale?.Sale?.Customer?.m?.specialOrdersSubtotal) - specialOrderFeeTotal
          : 0,
        taxTotal: toCents(sale?.Sale?.Customer?.m?.specialOrdersTaxTotal) || 0,
        discountTotal: toCents(sale?.Sale?.Customer?.m?.specialOrdersAllDiscounts) || 0,
        feeTotal: specialOrderFeeTotal || 0,
        totalAmount: toCents(sale?.Sale?.Customer?.m?.specialOrdersTotal) || 0,
      };
    case PricingTableTypes.SALE:
    case PricingTableTypes.QUOTE:
      return {
        subtotal: toCents(sale?.Sale?.displayableSubtotal) || 0,
        taxTotal: toCents(sale?.Sale?.taxTotal) || 0,
        discountTotal: toCents(sale?.Sale?.calcDiscount) || 0,
        feeTotal: toCents(sale?.Sale?.calcItemFees) || 0,
        totalAmount: toCents(sale?.Sale?.calcTotal) || 0,
      };
    case PricingTableTypes.WORKORDER:
      return {
        subtotal: toCents(sale?.Sale?.displayableSubtotal) || 0,
        taxTotal: toCents(sale?.Sale?.taxTotal) || 0,
        discountTotal: toCents(sale?.Sale?.calcDiscount) || 0,
        feeTotal: toCents(sale?.Sale?.calcItemFees) || 0,
        totalAmount: toCents(sale?.Sale?.calcTotal) || 0,
      };
    default:
      return { subtotal: 0, taxTotal: 0, discountTotal: 0, feeTotal: 0, totalAmount: 0 };
  }
}

export function getSaleLinesPerPricingTableType(
  type: PricingTableType,
  sale: AggregateSaleData
): SaleLine[] {
  switch (type) {
    case PricingTableTypes.LAYAWAY:
      return normalizeArray<SaleLine>(sale?.Sale?.Customer?.Layaways?.SaleLine);
    case PricingTableTypes.SPECIAL_ORDER:
      return normalizeArray<SaleLine>(sale?.Sale?.Customer?.SpecialOrders?.SaleLine);
    case PricingTableTypes.SALE:
    case PricingTableTypes.WORKORDER:
    case PricingTableTypes.QUOTE:
      return normalizeArray<SaleLine>(sale?.Sale?.SaleLines?.SaleLine);
    default:
      return [];
  }
}

export function buildLineItems(saleLines: SaleLine[] | SaleLine): LineItem[] {
  const lines = normalizeArray(saleLines);
  const lineItems: LineItem[] = lines.map((saleLine: SaleLine): LineItem => {
    const tax1 = Number(saleLine.calcTax1) || 0;
    const tax2 = Number(saleLine.calcTax2) || 0;
    const lineItem: LineItem = {
      lineItemRef: String(saleLine.saleLineID ?? ''),
      quantity: Number(saleLine.unitQuantity) || 0,
      unitAmount: toCents(saleLine.unitPrice) || 0,
      name: String(saleLine.Item?.description ?? ''),
      description: String(saleLine.Note?.note ?? ''),
      taxes: buildLineItemTaxes(saleLine),
      discounts: saleLine.Discount ? buildLineItemDiscounts(saleLine) : [],
      fees: [],
      // totals for sale extended from Totals{}
      subtotal: toCents(saleLine.displayableSubtotal) || 0,
      taxTotal: toCents(tax1 > 0 ? tax1 : tax2),
      discountTotal: toCents(saleLine.calcLineDiscount) || 0,
      feeTotal: 0,
      totalAmount: toCents(saleLine.calcTotal) || 0,
      ...(saleLine.SaleLineWorkOrder?.workOrderId
        ? { workOrderRef: String(saleLine.SaleLineWorkOrder.workOrderId) }
        : {}),
    };

    return lineItem;
  });

  const byId: Map<string, LineItem> = new Map(
    lineItems.map((li: LineItem) => [li.lineItemRef, li])
  );

  lines
    .filter((saleLine: SaleLine) => lineItemContainsFees(saleLine))
    .forEach((feeLine: SaleLine) => {
      const parentId: string = String(feeLine.parentSaleLineID);
      const parent: LineItem | undefined = byId.get(parentId);
      if (!parent) return;

      const feeAmount: number = toCents(feeLine.ItemFee?.feeValue);

      parent.fees.push({
        feeRef: String(feeLine.ItemFee?.itemFeeID),
        name: String(feeLine.ItemFee?.name),
        totalAmount: feeAmount,
      });

      parent.feeTotal += feeAmount;
    });

  return lines
    .filter(
      (saleLine: SaleLine) => !lineItemContainsFees(saleLine) && !isParentWorkOrderLine(saleLine)
    )
    .map((saleLine: SaleLine) => byId.get(String(saleLine.saleLineID))!)
    .filter(Boolean);
}

export function buildLineItemTaxes(saleLine: SaleLine): PaymentRequestTax[] {
  if (String(saleLine.tax) === 'false') return [];

  const taxes: PaymentRequestTax[] = [];

  const tax1Amount = Number(saleLine.calcTax1) || 0;
  if (tax1Amount > 0) {
    const taxableAmount = Number(saleLine.calcTotal) - tax1Amount;
    const calculatedRate = taxableAmount > 0 ? tax1Amount / taxableAmount : 0;

    taxes.push({
      taxRef: String(saleLine.taxClassID ?? ''),
      name: String(saleLine.TaxClass?.name ?? ''),
      rate: calculatedRate,
      totalAmount: toCents(tax1Amount),
      type: '',
    });
  }

  const tax2Amount = Number(saleLine.calcTax2) || 0;
  if (tax2Amount > 0) {
    const taxableAmount = Number(saleLine.calcTotal) - tax2Amount;
    const calculatedRate = taxableAmount > 0 ? tax2Amount / taxableAmount : 0;

    taxes.push({
      taxRef: String(saleLine.taxClassID ?? ''),
      name: String(saleLine.TaxClass?.name ?? ''),
      rate: calculatedRate,
      totalAmount: toCents(tax2Amount),
      type: '',
    });
  }

  return taxes;
}

export function buildLineItemDiscounts(saleLine: SaleLine): PaymentRequestDiscount[] {
  return [
    {
      discountRef: String(saleLine.Discount?.discountID) || '',
      name: String(saleLine.Discount?.name) || '',
      unitAmount: toCents(getUnitDiscount(saleLine)),
      totalAmount: toCents(saleLine.calcLineDiscount),
      percent: parseFloat(saleLine.discountPercent) || 0,
      type: parseDiscountType(saleLine),
    },
  ];
}

export function parseDiscountType(saleLine: SaleLine): DiscountType {
  return Number(saleLine.Discount?.discountAmount) === 0
    ? DiscountType.PERCENT
    : DiscountType.FIXED;
}

export function getUnitDiscount(saleLine: SaleLine): number {
  if (saleLine.unitQuantity === '0') {
    return 0;
  }
  return Number(saleLine.calcLineDiscount ?? 0) / Number(saleLine.unitQuantity ?? 1);
}

export function getAllDiscountsForTable(
  type: PricingTableType,
  sale: AggregateSaleData
): PaymentRequestDiscount[] {
  const saleLines = getSaleLinesPerPricingTableType(type, sale);
  return getAllDiscounts(saleLines);
}

function getAllDiscounts(saleLines: SaleLine[] | SaleLine): PaymentRequestDiscount[] {
  return normalizeArray(saleLines)
    .filter((l) => !isParentWorkOrderLine(l))
    .flatMap((l) => (l.Discount ? buildLineItemDiscounts(l) : []));
}

export function getAllFeesForTable(type: PricingTableType, sale: AggregateSaleData): Fee[] {
  const saleLines = getSaleLinesPerPricingTableType(type, sale);
  return getAllFees(saleLines);
}

export function getAllFees(saleLines: SaleLine[] | SaleLine): Fee[] {
  return normalizeArray(saleLines)
    .filter(
      (l) =>
        String(l.lineType) === 'item_fee' || (l.itemFeeID != null && String(l.itemFeeID) !== '0')
    )
    .map((l) => ({
      feeRef: String(l.itemFeeID ?? l.ItemFee?.itemFeeID ?? ''),
      name: String(l.ItemFee?.name ?? 'Fee'),
      totalAmount: toCents(String(l.calcSubtotal ?? l.displayableSubtotal ?? '0')) || 0,
    }));
}

function calculateFeeTotal(saleLines: SaleLine[] | SaleLine): number {
  return normalizeArray(saleLines).reduce((feeTotal, line) => {
    if (line.lineType === 'item_fee') {
      const feeAmount = toCents(line.calcTotal) || 0;
      return feeTotal + feeAmount;
    }
    return feeTotal;
  }, 0);
}

export function lineItemContainsFees(lineItem: SaleLine): boolean {
  return String(lineItem.lineType) === 'item_fee';
}

export function isParentWorkOrderLine(saleLine: SaleLine): boolean {
  return saleLine.SaleLineWorkOrder?.workOrderType === 'parent';
}

export function buildWorkOrders(saleLines: SaleLine[] | SaleLine): WorkOrder[] {
  return normalizeArray(saleLines)
    .filter(isParentWorkOrderLine)
    .map((saleLine) => ({
      saleLineRef: String(saleLine.saleLineID ?? ''),
      workOrderRef: String(saleLine.SaleLineWorkOrder?.workOrderId ?? ''),
      workOrderType: String(saleLine.SaleLineWorkOrder?.workOrderType ?? ''),
      workOrderNote: String(saleLine.Note?.note ?? ''),
    }));
}

export async function buildAllTaxes(
  type: PricingTableType,
  sale: AggregateSaleData,
  accountID: string
): Promise<PaymentRequestTax[]> {
  switch (type) {
    case PricingTableTypes.LAYAWAY:
    case PricingTableTypes.SPECIAL_ORDER:
      const specialOrderAndLayawayTaxes = await buildSpecialOrderAndLayawayTaxes(
        type,
        sale,
        accountID
      );
      return specialOrderAndLayawayTaxes;
    case PricingTableTypes.SALE:
    case PricingTableTypes.QUOTE:
      return Promise.resolve(buildSaleTaxes(sale));
    case PricingTableTypes.WORKORDER:
      return Promise.resolve(buildWorkorderTaxes(sale));
    default:
      return Promise.resolve([]);
  }
}

export function buildWorkorderTaxes(sale: AggregateSaleData): PaymentRequestTax[] {
  return [
    {
      taxRef: '1',
      name: 'Tax',
      totalAmount: sale.Sale?.m?.tax ? toCents(sale.Sale?.m?.tax) : 0,
      type: 'Tax',
    },
  ];
}

export function buildSaleTaxes(sale: AggregateSaleData): PaymentRequestTax[] {
  const taxes = sale?.Sale?.TaxClassTotals?.Tax;

  if (!taxes) return [];

  const taxArray = Array.isArray(taxes) ? taxes : [taxes];

  return taxArray.flatMap((tax: Tax): PaymentRequestTax[] => {
    const result: PaymentRequestTax[] = [];

    // Add tax1 if it exists
    if (tax.amount && Number(tax.amount) > 0) {
      result.push({
        taxRef: String(tax.id ?? ''),
        name: String(tax.name ?? ''), // LSR Tax class name ex: Item or Labor
        rate: Number(tax.rate ?? 0),
        totalAmount: toCents(tax.amount ?? 0),
        type: String(tax.taxname ?? ''), // Tax name ex: GST or PST
      });
    }

    // Add tax2 if it exists
    // When taxes have 2 components (e.g., GST + PST), 2nd tax information will be defined within the following props
    if (tax.amount2 && Number(tax.amount2) > 0) {
      result.push({
        taxRef: String(tax.id ?? ''),
        name: String(tax.name ?? ''), // LSR Tax class name ex: Item or Labor
        rate: Number(tax.rate2 ?? 0),
        totalAmount: toCents(tax.amount2 ?? 0),
        type: String(tax.taxname2 ?? ''), // Tax name ex: GST or PST
      });
    }

    return result;
  });
}

export async function buildSpecialOrderAndLayawayTaxes(
  type: PricingTableType,
  sale: AggregateSaleData,
  accountID: string
): Promise<PaymentRequestTax[]> {
  const saleLines = getSaleLinesPerPricingTableType(type, sale);

  // filter for taxable sale lines
  const taxableSaleLines = saleLines.filter((l) => String(l.tax) === 'true') ?? [];
  if (taxableSaleLines.length === 0) return [];

  //  find all taxed sale lines, group them by taxClassID, and determine each group's tax rate and total
  const groupedSaleLines = taxableSaleLines
    .map((line) => ({ id: String(line.taxClassID ?? ''), line }))
    .filter(({ id }) => id && id !== '0')
    .reduce<Map<string, SaleLine[]>>((taxClassToSaleLinesMap, { id, line }) => {
      const existingSaleLines = taxClassToSaleLinesMap.get(id);
      if (existingSaleLines) existingSaleLines.push(line);
      else taxClassToSaleLinesMap.set(id, [line]);
      return taxClassToSaleLinesMap;
    }, new Map());
  if (groupedSaleLines.size === 0) return [];

  // fetch tax class names for each taxClassID
  const taxClassIDs = Array.from(groupedSaleLines.keys());
  const taxClassNames = await Promise.all(
    taxClassIDs.map((id) => fetchTaxClassName(id, accountID))
  );

  // map each taxClassID to its tax class name
  const taxClassIdToName = taxClassIDs.reduce<Map<string, string>>((taxClassMap, id, i) => {
    taxClassMap.set(id, taxClassNames[i] ?? '');
    return taxClassMap;
  }, new Map());

  // calculate the total tax rate for each group of sale lines by taxClassID
  const taxTotals: PaymentRequestTax[] = [];
  Array.from(groupedSaleLines.entries()).forEach(([taxClassID, linesInGroup]) => {
    const { taxAmountSum, taxableBaseSum } = linesInGroup.reduce(
      (taxAggregates, saleLine) => {
        const calcTax1 = Number(String(saleLine.calcTax1 ?? '0'));
        const calcTax2 = Number(String(saleLine.calcTax2 ?? '0'));
        const subtotal = Number(String(saleLine.calcSubtotal ?? '0'));
        const lineDiscount = Number(String(saleLine.calcLineDiscount ?? '0'));

        const taxAmount = calcTax1 > 0 ? calcTax1 : calcTax2;
        const taxableBase = subtotal - lineDiscount;

        taxAggregates.taxAmountSum += taxAmount;
        taxAggregates.taxableBaseSum += Math.max(taxableBase, 0);
        return taxAggregates;
      },
      { taxAmountSum: 0, taxableBaseSum: 0 }
    );

    const taxRate = taxableBaseSum > 0 ? (taxAmountSum / taxableBaseSum) * 100 : 0;

    taxTotals.push({
      taxRef: taxClassID || '',
      name: taxClassIdToName.get(taxClassID) || '',
      rate: parseFloat(taxRate.toFixed(2)),
      type: 'Tax', // TODO will be tackled in QM-5642
      totalAmount: toCents(taxAmountSum),
    });
  });

  return taxTotals;
}

export async function fetchTaxClassName(
  taxClassID: string,
  accountID: string
): Promise<string | undefined> {
  const url = `/API/V3/Account/${accountID}/TaxClass/${taxClassID}.json`;

  try {
    const { data } = await axios.get<{ TaxClass?: { name?: string } }>(url, {
      params: { load_relations: 'all' },
    });
    return data?.TaxClass?.name;
  } catch (error) {
    datadogRum.addError(error, {
      message: `Failed to fetch TaxClass for taxRef: ${taxClassID} and for accountID: ${accountID} `,
      timeStamp: new Date().toISOString(),
    });
  }
  return undefined;
}

export function buildCustomer(sale: AggregateSaleData): PaymentRequestCustomer {
  const customer = sale.Sale?.Customer;
  return {
    customerRef: String(customer?.customerID ?? ''),
    creditAccountRef: String(customer?.creditAccountID ?? ''),
    firstName: String(customer?.firstName ?? ''),
    lastName: String(customer?.lastName ?? ''),
    email: getCustomerEmail(customer),
  };
}

export function getCustomerEmail(customer?: Customer): string {
  return extractEmail(customer?.Contact?.Emails);
}

export function buildLocation(sale: AggregateSaleData): Location {
  const shop = sale.Sale?.Shop;
  return {
    locationRef: String(shop?.shopID ?? ''),
    name: String(shop?.name ?? ''),
    email: getLocationEmail(shop),
  };
}

export function getLocationEmail(shop?: Shop): string {
  return extractEmail(shop?.Contact?.Emails);
}

function extractEmail(emails?: Contact['Emails'] | ''): string {
  if (!emails || typeof emails !== 'object') return '';
  const contactEmail = emails.ContactEmail;
  if (!contactEmail) return '';
  return Array.isArray(contactEmail) ? contactEmail[0]?.address ?? '' : contactEmail.address ?? '';
}

export function normalizeArray<T>(array: T | T[] | undefined | null): T[] {
  if (!array) return [];
  return Array.isArray(array) ? array : [array];
}

export // help convert payload
const toCents = (v: number | string | undefined | null) =>
  new Decimal(v || 0).multipliedBy(100).decimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();
